import { styled } from '@linaria/react';
import { MultiSelect, SegmentedControl, Select, TextInput } from '@mantine/core';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutomationEventType,
  AutomationTriggerType,
  type EventFilterDefinition,
  type EventPresetDefinition,
  type FilterFieldDefinition,
  type FilterRule,
  type FilterRuleValue,
  type FilterRules,
  FilterMode,
  FilterOperator,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { FloraFeedbackSentiment } from '../../../../api/flora-feedback-client/flora-feedback-client.type';
import { useInitiatives } from '../../../../api/initiative-client/initiative-client.hooks';
import { usePortfolios } from '../../../../api/portfolio-client/portfolio-client.hooks';
import { useProjects } from '../../../../api/projects-client/projects-client.hooks';
import { useTargetsData } from '../../../../api/targets-client/targets-client.hooks';
import {
  getTaskEventTypeOptions,
  MAPPING_RELATIONSHIP_OPTIONS,
} from '../../../../api/workflows-client/workflows-client.type';
import { useGetWorkflows } from '../../../../api/workflows-client/workflows.client.hooks';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { AgentScheduleBuilder } from '../agent-schedule-builder/agent-schedule-builder';
import { inputStyles } from '../shared/input-styles';
import {
  DEFAULT_FILTER_FIELD_ICON,
  DEFAULT_PRESET_ICON,
  FILTER_FIELD_ICONS,
  PRESET_ICONS,
  PRIORITY_OPTIONS,
  TASK_DIRECTION_OPTIONS,
  TASK_FIELD_TYPE_OPTIONS,
  TASK_SECTION_OPTIONS,
  TASK_TYPE_OPTIONS,
  VALUE_DIRECTION_OPTIONS,
} from './agent-automations.constants';
import {
  applyRulePatchWithCascade,
  blankRuleForField,
  buildAutomationFilterOptions,
  buildPresetFilterRules,
  getEventIcon,
  getOperatorLabel,
  isBooleanOperator,
  isMultiValueOperator,
} from './agent-automations.helpers';

type OptionItem = { value: string; label: string };

type UseFilterOptionsResult = {
  options: OptionItem[];
  loading: boolean;
  emptyMessage: string;
  pickerPlaceholder: string;
  hasSource: boolean;
};

type UseFilterOptionsContext = {
  // When the active rule's options_source is `workflow_statuses`, the picker
  // needs to know which workflow_id sibling rule was chosen so it can scope
  // the status list to that workflow's step mappings.
  selectedWorkflowId: string | null;
};

/**
 * Builds the uniform resolver result for an options source backed by a static
 * list rather than a data hook.
 *
 * @param {OptionItem[]} options - The static option list
 * @param {string} pickerPlaceholder - Placeholder shown in the picker
 * @param {string} emptyMessage - Message when the list is empty (rare for static sources)
 * @returns {UseFilterOptionsResult} A non-loading result with `hasSource: true`
 */
function staticOptionsResult(
  options: OptionItem[],
  pickerPlaceholder: string,
  emptyMessage = '',
): UseFilterOptionsResult {
  return { options, loading: false, emptyMessage, pickerPlaceholder, hasSource: true };
}

/**
 * Resolves a filter field's `options_source` token into a list of dropdown options for the editor.
 *
 * Responsibilities:
 * 1. Decide which existing client hook or static list backs the source (targets, projects, boards, portfolios, initiatives, workflows, workflow_statuses, task_types, priorities, task_sections, task_directions, task_field_types, …)
 * 2. Always call every hook so React's hooks order stays stable across renders
 * 3. Return a uniform `{options, loading, ...}` regardless of source
 *
 * @param {string | null | undefined} optionsSource - The `options_source` string from the BE filter registry
 * @param {UseFilterOptionsContext} context - Sibling-rule context the resolver may need (e.g. selected workflow for status scoping)
 * @returns {UseFilterOptionsResult} Options for the picker, loading state, and labels
 */
function useFilterOptions(
  optionsSource: string | null | undefined,
  context: UseFilterOptionsContext,
): UseFilterOptionsResult {
  const { t } = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const activePortfolioId = useGlobalStore((state) => state.portfolio?.id);

  const { data: targets } = useTargetsData();
  const { projects, query: projectsQuery } = useProjects({
    organizationId,
    queryKey: ['projects', organizationId ?? null],
  });
  const { portfolios, query: portfoliosQuery } = usePortfolios();
  const { initiatives, query: initiativesQuery } = useInitiatives(activePortfolioId, {
    queryKey: ['initiatives', activePortfolioId ?? null],
    enabled: !!activePortfolioId,
  });
  // Org-wide workflows (no portfolio filter) — a skipped_status automation
  // can be scoped to any workflow regardless of the user's current portfolio.
  const { data: workflows, query: workflowsQuery } = useGetWorkflows(organizationId ?? null, null);

  return useMemo<UseFilterOptionsResult>(() => {
    switch (optionsSource) {
      case 'targets':
        return {
          options: buildAutomationFilterOptions(
            (targets ?? []).map((target) => ({ value: target.id, label: target.name })),
          ),
          loading: false,
          emptyMessage: 'No targets found',
          pickerPlaceholder: 'Pick a target',
          hasSource: true,
        };
      case 'measures': {
        // The "configured measures" set = the distinct `measure` field across
        // all of the org's targets. Targets are the only place a measure is
        // wired to an emitted event.
        const distinct = new Set<string>();
        for (const target of targets ?? []) {
          if (target.measure) distinct.add(target.measure);
        }
        const measures = [...distinct].sort();
        return {
          options: measures.map((measure) => ({ value: measure, label: measure })),
          loading: false,
          emptyMessage: 'No configured measures found',
          pickerPlaceholder: 'Pick a measure',
          hasSource: true,
        };
      }
      case 'projects':
        return {
          options: buildAutomationFilterOptions(
            (projects ?? []).map((project) => ({ value: project.id, label: project.name })),
          ),
          loading: projectsQuery.isLoading,
          emptyMessage: 'No projects found',
          pickerPlaceholder: 'Pick a project',
          hasSource: true,
        };
      case 'boards':
        return {
          options: (projects ?? []).flatMap((project) =>
            project.subprojects.map((subproject) => ({
              value: subproject.id,
              label: `${project.name} — ${subproject.name}`,
            })),
          ),
          loading: projectsQuery.isLoading,
          emptyMessage: 'No boards found',
          pickerPlaceholder: 'Pick a board',
          hasSource: true,
        };
      case 'portfolios': {
        // fetchPortfolios returns portfolios across every org the user belongs
        // to; the BE doesn't accept an org filter. Narrow to the active org
        // here so the picker only shows valid options for this automation.
        const scoped = organizationId
          ? portfolios.filter((portfolio) => portfolio.organization.id === organizationId)
          : portfolios;
        return {
          options: buildAutomationFilterOptions(
            scoped.map((portfolio) => ({ value: portfolio.id, label: portfolio.name })),
          ),
          loading: portfoliosQuery.isLoading,
          emptyMessage: 'No portfolios found',
          pickerPlaceholder: 'Pick a portfolio',
          hasSource: true,
        };
      }
      case 'initiatives':
        return {
          options: buildAutomationFilterOptions(
            (initiatives ?? []).map((initiative) => ({
              value: initiative.id,
              label: initiative.name,
            })),
          ),
          loading: initiativesQuery.isLoading,
          emptyMessage: activePortfolioId
            ? 'No initiatives in this portfolio'
            : 'Select a portfolio in the sidebar to load initiatives',
          pickerPlaceholder: 'Pick an initiative',
          hasSource: true,
        };
      case 'workflows':
        return {
          options: buildAutomationFilterOptions(
            (workflows ?? []).map((workflow) => ({ value: workflow.id, label: workflow.name })),
          ),
          loading: workflowsQuery.isLoading,
          emptyMessage: 'No workflows configured',
          pickerPlaceholder: 'Pick a workflow',
          hasSource: true,
        };
      case 'workflow_statuses': {
        // Statuses are scoped to the sibling workflow_id rule. The
        // skipped_status event requires a workflow_id in filter_rules, so the
        // FE auto-seeds one — but the user has to actually pick a value
        // before status options can populate.
        if (!context.selectedWorkflowId) {
          return {
            options: [],
            loading: workflowsQuery.isLoading,
            emptyMessage: 'Pick a workflow first',
            pickerPlaceholder: 'Pick a status',
            hasSource: true,
          };
        }
        const workflow = workflows?.find((entry) => entry.id === context.selectedWorkflowId);
        const distinct = new Set<string>();
        for (const step of workflow?.steps ?? []) {
          for (const mapping of step.mappings) {
            if (mapping.external_name) distinct.add(mapping.external_name);
          }
        }
        const statuses = [...distinct].sort();
        return {
          options: statuses.map((status) => ({ value: status, label: status })),
          loading: workflowsQuery.isLoading,
          emptyMessage: 'Selected workflow has no mapped statuses',
          pickerPlaceholder: 'Pick a status',
          hasSource: true,
        };
      }
      case 'task_event_types':
        return staticOptionsResult(getTaskEventTypeOptions(t), 'Pick an event type', 'No event types');
      case 'task_event_relationships':
        return staticOptionsResult(MAPPING_RELATIONSHIP_OPTIONS, 'Pick a relationship');
      case 'flora_feedback_sentiments':
        return staticOptionsResult(
          [
            { value: FloraFeedbackSentiment.Positive, label: 'Positive' },
            { value: FloraFeedbackSentiment.Negative, label: 'Negative' },
          ],
          'Pick a sentiment',
        );
      case 'priorities':
        return staticOptionsResult(PRIORITY_OPTIONS, 'Pick a priority');
      case 'task_sections':
        return staticOptionsResult(TASK_SECTION_OPTIONS, 'Pick a section');
      case 'task_directions':
        return staticOptionsResult(TASK_DIRECTION_OPTIONS, 'Pick a direction');
      case 'value_directions':
        return staticOptionsResult(VALUE_DIRECTION_OPTIONS, 'Pick a direction');
      case 'task_field_types':
        return staticOptionsResult(TASK_FIELD_TYPE_OPTIONS, 'Pick a field');
      case 'task_types':
        return staticOptionsResult(TASK_TYPE_OPTIONS, 'Pick a task type');
      default:
        return { options: [], loading: false, emptyMessage: '', pickerPlaceholder: '', hasSource: false };
    }
  }, [
    optionsSource,
    organizationId,
    targets,
    projects,
    projectsQuery.isLoading,
    portfolios,
    portfoliosQuery.isLoading,
    initiatives,
    initiativesQuery.isLoading,
    activePortfolioId,
    workflows,
    workflowsQuery.isLoading,
    context.selectedWorkflowId,
    t,
  ]);
}

const TRIGGER_SOURCE_OPTIONS = [
  { label: 'Event hook', value: AutomationTriggerType.EVENT },
  { label: 'On schedule', value: AutomationTriggerType.CRON },
  { label: 'Manual', value: AutomationTriggerType.MANUAL },
];

const dropdownChevron = <Icon name="expand_more" color="var(--sky-dark)" size={18} />;

interface AgentAutomationTriggerConfigProps {
  triggerType: AutomationTriggerType;
  eventType: AutomationEventType | null;
  filterRules: FilterRules;
  cronInitialValue: string | null;
  definitions: EventFilterDefinition[];
  definitionsLoading: boolean;
  onChangeTriggerType: (triggerType: AutomationTriggerType) => void;
  onChangeEventType: (eventType: AutomationEventType | null) => void;
  onChangeFilterRules: (filterRules: FilterRules) => void;
  onChangeCron: (cronExpression: string | null) => void;
  onChangeCronValidity: (isValid: boolean) => void;
}

export function AgentAutomationTriggerConfig({
  triggerType,
  eventType,
  filterRules,
  cronInitialValue,
  definitions,
  definitionsLoading,
  onChangeTriggerType,
  onChangeEventType,
  onChangeFilterRules,
  onChangeCron,
  onChangeCronValidity,
}: AgentAutomationTriggerConfigProps) {
  const eventOptions = useMemo(
    () => definitions.map((def) => ({ value: def.event_type, label: def.label })),
    [definitions],
  );
  const eventDefinition = useMemo<EventFilterDefinition | undefined>(
    () => definitions.find((def) => def.event_type === eventType),
    [definitions, eventType],
  );

  const requiredFieldNames = useMemo<Set<string>>(
    () => new Set(eventDefinition?.required_filters ?? []),
    [eventDefinition],
  );

  const missingRequiredFields = useMemo<FilterFieldDefinition[]>(() => {
    if (!eventDefinition) return [];
    const presentFields = new Set(filterRules.rules.map((rule) => rule.field));
    return eventDefinition.optional_filters.filter(
      (field) => requiredFieldNames.has(field.field) && !presentFields.has(field.field),
    );
  }, [eventDefinition, filterRules.rules, requiredFieldNames]);

  const presentFields = useMemo<Set<string>>(
    () => new Set(filterRules.rules.map((rule) => rule.field)),
    [filterRules.rules],
  );

  const canAddAnotherRule = useMemo<boolean>(() => {
    if (!eventDefinition) return false;
    // Required fields are single-instance; optional fields can repeat. Hide
    // the "Add filter" button only when *every* required slot is filled and
    // there are no optional fields available at all.
    return eventDefinition.optional_filters.some(
      (field) => !requiredFieldNames.has(field.field) || !presentFields.has(field.field),
    );
  }, [eventDefinition, requiredFieldNames, presentFields]);

  const handleApplyPreset = useCallback(
    (preset: EventPresetDefinition): void => {
      const next = buildPresetFilterRules(preset);
      // Applying a recipe replaces the rules, but an already-chosen project
      // scope is deliberate — carry the existing project_id rule over verbatim
      // (field + operator + value, including multi-project `is_any_of` arrays).
      // Only the seeded blank rule remains when no project rule exists yet.
      const currentProject = filterRules.rules.find((rule) => rule.field === 'project_id');
      if (currentProject) {
        onChangeFilterRules({
          ...next,
          rules: next.rules.map((rule) => (rule.field === 'project_id' ? { ...currentProject } : rule)),
        });
        return;
      }
      onChangeFilterRules(next);
    },
    [filterRules.rules, onChangeFilterRules],
  );

  const handleAddRule = useCallback((): void => {
    if (!eventDefinition || eventDefinition.optional_filters.length === 0) return;
    const fieldToAdd =
      missingRequiredFields[0] ??
      eventDefinition.optional_filters.find(
        (field) => !requiredFieldNames.has(field.field) || !presentFields.has(field.field),
      );
    if (!fieldToAdd) return;
    onChangeFilterRules({
      ...filterRules,
      rules: [...filterRules.rules, blankRuleForField(fieldToAdd.field, fieldToAdd.operators)],
    });
  }, [eventDefinition, missingRequiredFields, requiredFieldNames, presentFields, filterRules, onChangeFilterRules]);

  const handleAddRequiredField = useCallback(
    (field: FilterFieldDefinition): void => {
      onChangeFilterRules({
        ...filterRules,
        rules: [...filterRules.rules, blankRuleForField(field.field, field.operators)],
      });
    },
    [filterRules, onChangeFilterRules],
  );

  const handleUpdateRule = useCallback(
    (index: number, patch: Partial<FilterRule>): void => {
      onChangeFilterRules({
        ...filterRules,
        rules: applyRulePatchWithCascade(filterRules.rules, index, patch, eventType),
      });
    },
    [eventType, filterRules, onChangeFilterRules],
  );

  const handleToggleFilterMode = useCallback((): void => {
    onChangeFilterRules({
      ...filterRules,
      mode: filterRules.mode === FilterMode.ALL ? FilterMode.ANY : FilterMode.ALL,
    });
  }, [filterRules, onChangeFilterRules]);

  const handleRemoveRule = useCallback(
    (index: number): void => {
      const target = filterRules.rules[index];
      // Defense in depth: the row's remove button is disabled for required
      // fields, but a stray keyboard call shouldn't blow past it.
      if (target && requiredFieldNames.has(target.field)) return;
      onChangeFilterRules({
        ...filterRules,
        rules: filterRules.rules.filter((_, ruleIndex) => ruleIndex !== index),
      });
    },
    [filterRules, onChangeFilterRules, requiredFieldNames],
  );

  return (
    <Wrapper>
      <Section>
        <SectionLabel id="automation-trigger-source-label">Trigger source</SectionLabel>
        <SegmentedControl
          value={triggerType}
          onChange={(next) => onChangeTriggerType(next as AutomationTriggerType)}
          data={TRIGGER_SOURCE_OPTIONS}
          fullWidth
          aria-labelledby="automation-trigger-source-label"
          styles={{
            root: { borderRadius: 8, background: 'var(--sky-lighter)' },
            label: { color: 'var(--ink-dark)' },
            indicator: { background: 'var(--base-white)', boxShadow: 'var(--box-shadow-small-soft)' },
          }}
        />
      </Section>

      {triggerType === AutomationTriggerType.MANUAL && (
        <Section>
          <HelperText>
            Manual automations only fire when you trigger them yourself — no schedule, no event hooks. Use this to draft
            and dry-run a flow before wiring it up to anything.
          </HelperText>
        </Section>
      )}

      {triggerType === AutomationTriggerType.CRON && (
        <Section>
          <SectionLabel>Schedule</SectionLabel>
          <AgentScheduleBuilder
            value={cronInitialValue}
            onChange={onChangeCron}
            onValidityChange={onChangeCronValidity}
            fieldIdPrefix="automation-schedule"
          />
        </Section>
      )}

      {triggerType === AutomationTriggerType.EVENT && (
        <Section>
          <SectionLabel htmlFor="automation-event">Event</SectionLabel>
          <Select
            id="automation-event"
            placeholder={definitionsLoading ? 'Loading events…' : 'Pick a Bloomfilter event'}
            value={eventType}
            onChange={(value) => onChangeEventType(value as AutomationEventType | null)}
            data={eventOptions}
            disabled={definitionsLoading}
            allowDeselect={false}
            leftSection={
              eventType ? (
                <Icon name={getEventIcon(eventType)} color="var(--amber-base, #d98e2b)" size={18} />
              ) : undefined
            }
            rightSection={dropdownChevron}
            rightSectionPointerEvents="none"
            renderOption={({ option, checked }) => (
              <EventOption>
                <Icon
                  name={getEventIcon(option.value as AutomationEventType)}
                  color="var(--amber-base, #d98e2b)"
                  size={18}
                />
                {option.label}
                {checked && <Icon name="check" color="var(--ink-light)" size={16} style={{ marginLeft: 'auto' }} />}
              </EventOption>
            )}
            styles={inputStyles}
          />
          <HelperText>Fires every time this event is emitted. Add filters below to narrow it down.</HelperText>
        </Section>
      )}

      {triggerType === AutomationTriggerType.EVENT && eventDefinition && (eventDefinition.presets ?? []).length > 0 && (
        <Section>
          <SectionLabel id="automation-preset-label">Quick presets</SectionLabel>
          <PresetChipRow role="group" aria-labelledby="automation-preset-label">
            {(eventDefinition.presets ?? []).map((preset) => (
              <PresetChip
                key={preset.key}
                type="button"
                aria-label={preset.label}
                title={preset.description}
                onClick={() => handleApplyPreset(preset)}
              >
                <Icon name={PRESET_ICONS[preset.key] ?? DEFAULT_PRESET_ICON} color="var(--secondary-base)" size={13} />
                {preset.label}
              </PresetChip>
            ))}
          </PresetChipRow>
          <HelperText>Applies a ready-made filter recipe — you can tweak the rules after.</HelperText>
        </Section>
      )}

      {triggerType === AutomationTriggerType.EVENT && eventDefinition && (
        <Section>
          <FilterHeader>
            <SectionLabel id="automation-filter-label">Filters</SectionLabel>
            {filterRules.rules.length > 0 && <ActiveCount>{filterRules.rules.length} active</ActiveCount>}
          </FilterHeader>
          {missingRequiredFields.length > 0 && (
            // A single live region: per-banner role="status" would announce as
            // competing regions and go silent when a banner unmounts on Add.
            <RequiredBannerGroup role="status">
              {missingRequiredFields.map((field) => (
                <RequiredBanner key={field.field}>
                  <RequiredBannerMessage>
                    <Icon name="error" color="var(--amber-base, #d98e2b)" size={16} />
                    <span>
                      <strong>{field.label}</strong> is required for this event
                    </span>
                  </RequiredBannerMessage>
                  <AddRequiredButton
                    type="button"
                    aria-label={`Add ${field.label} filter`}
                    onClick={() => handleAddRequiredField(field)}
                  >
                    <Icon name="add" color="var(--base-white)" size={14} />
                    Add
                  </AddRequiredButton>
                </RequiredBanner>
              ))}
            </RequiredBannerGroup>
          )}
          <RulesList>
            {filterRules.rules.length === 0 && (
              <HelperText>No filters — runs on every {eventDefinition.label.toLowerCase()} event.</HelperText>
            )}
            {filterRules.rules.map((rule, index) => (
              <Fragment
                // eventType in the key remounts rows when the event changes —
                // a same-keyed searchable Select would otherwise keep showing
                // its old label after the seed resets the value to ''.
                key={`${eventType}-${rule.field}-${index}`}
              >
                {index > 0 && (
                  <Connector>
                    <ConnectorLine />
                    <ConnectorPill
                      type="button"
                      onClick={handleToggleFilterMode}
                      aria-label={
                        filterRules.mode === FilterMode.ALL
                          ? 'Matching all filters — switch to any'
                          : 'Matching any filter — switch to all'
                      }
                    >
                      <Icon name="swap_horiz" color="var(--secondary-base)" size={15} />
                      {filterRules.mode === FilterMode.ALL ? 'and' : 'or'}
                    </ConnectorPill>
                    <ConnectorLine />
                  </Connector>
                )}
                <FilterRuleCard
                  rule={rule}
                  index={index}
                  isRequired={requiredFieldNames.has(rule.field)}
                  siblingRules={filterRules.rules}
                  definition={eventDefinition}
                  onUpdate={handleUpdateRule}
                  onRemove={handleRemoveRule}
                />
              </Fragment>
            ))}
          </RulesList>
          {canAddAnotherRule && (
            <AddRuleButton type="button" aria-label="Add filter" onClick={handleAddRule}>
              <Icon name="add_circle" color="var(--secondary-base)" size={18} />
              Add filter
            </AddRuleButton>
          )}
        </Section>
      )}
    </Wrapper>
  );
}

interface FilterRuleCardProps {
  rule: FilterRule;
  index: number;
  isRequired: boolean;
  siblingRules: FilterRule[];
  definition: EventFilterDefinition;
  onUpdate: (index: number, patch: Partial<FilterRule>) => void;
  onRemove: (index: number) => void;
}

function FilterRuleCard({
  rule,
  index,
  isRequired,
  siblingRules,
  definition,
  onUpdate,
  onRemove,
}: FilterRuleCardProps) {
  const requiredFieldNames = useMemo<Set<string>>(
    () => new Set(definition.required_filters),
    [definition.required_filters],
  );
  // Required fields are single-instance: omit them from this row's field
  // dropdown if a *sibling* rule already uses them. The row's own field stays
  // selectable so the dropdown always shows the current selection.
  const fieldOptions = useMemo(() => {
    const takenByOthers = new Set(siblingRules.filter((sibling) => sibling !== rule).map((sibling) => sibling.field));
    return definition.optional_filters
      .filter(
        (field) =>
          field.field === rule.field || !(requiredFieldNames.has(field.field) && takenByOthers.has(field.field)),
      )
      .map((field) => ({ value: field.field, label: field.label }));
  }, [definition.optional_filters, requiredFieldNames, rule, siblingRules]);
  const field = useMemo<FilterFieldDefinition | undefined>(
    () => definition.optional_filters.find((entry) => entry.field === rule.field),
    [definition.optional_filters, rule.field],
  );
  const operatorOptions = useMemo(
    () => (field ? field.operators.map((op) => ({ value: op, label: getOperatorLabel(op) })) : []),
    [field],
  );

  const handleFieldChange = (nextField: string | null): void => {
    if (!nextField) return;
    const nextDef = definition.optional_filters.find((entry) => entry.field === nextField);
    const operator = nextDef?.operators[0] ?? FilterOperator.IS;
    const patch: Partial<FilterRule> = { field: nextField, operator };
    if (isBooleanOperator(operator)) patch.value = undefined;
    else if (isMultiValueOperator(operator)) patch.value = [];
    else patch.value = '';
    onUpdate(index, patch);
  };

  const handleOperatorChange = (nextOperator: string | null): void => {
    if (!nextOperator) return;
    const patch: Partial<FilterRule> = { operator: nextOperator };
    if (isBooleanOperator(nextOperator)) patch.value = undefined;
    else if (isMultiValueOperator(nextOperator)) patch.value = [];
    else patch.value = '';
    onUpdate(index, patch);
  };

  const fieldIcon = FILTER_FIELD_ICONS[field?.options_source ?? ''] ?? DEFAULT_FILTER_FIELD_ICON;

  return (
    <RuleCard>
      <RuleCardHeader>
        <RuleCardField>
          <Icon name={fieldIcon} color="var(--secondary-base)" size={17} />
          {isRequired ? (
            // Required fields are fixed by the event definition — a plain
            // label instead of a disabled picker.
            <RuleCardFieldName>{field?.label ?? rule.field}</RuleCardFieldName>
          ) : (
            <FieldSelectWrapper>
              <Select
                value={rule.field}
                onChange={handleFieldChange}
                data={fieldOptions}
                allowDeselect={false}
                aria-label="Filter field"
                variant="unstyled"
                // The content-sized input collapses the picker to the field
                // name's width, so the dropdown needs its own fixed width.
                comboboxProps={{ width: 220, position: 'bottom-start' }}
                size="xs"
              />
            </FieldSelectWrapper>
          )}
        </RuleCardField>
        <RuleCardHeaderActions>
          {isRequired && <RequiredBadge>Required</RequiredBadge>}
          <RemoveButton
            type="button"
            onClick={() => onRemove(index)}
            aria-label={isRequired ? 'Required filter — cannot be removed' : 'Remove filter'}
            title={isRequired ? 'This trigger requires this filter — change the event type to remove it.' : undefined}
            disabled={isRequired}
          >
            <Icon name="close" color="var(--ink-lighter)" size={15} />
          </RemoveButton>
        </RuleCardHeaderActions>
      </RuleCardHeader>
      <RuleCardBody>
        <Select
          value={rule.operator}
          onChange={handleOperatorChange}
          data={operatorOptions}
          allowDeselect={false}
          aria-label="Filter operator"
          rightSection={dropdownChevron}
          rightSectionPointerEvents="none"
          styles={{ ...inputStyles, root: { flex: '0 0 130px' } }}
          size="xs"
        />
        <ValueControlWrapper>
          <FilterValueControl
            rule={rule}
            field={field}
            siblingRules={siblingRules}
            onChange={(value) => onUpdate(index, { value })}
          />
        </ValueControlWrapper>
      </RuleCardBody>
    </RuleCard>
  );
}

interface FilterValueControlProps {
  rule: FilterRule;
  field: FilterFieldDefinition | undefined;
  siblingRules: FilterRule[];
  onChange: (value: FilterRuleValue) => void;
}

function FilterValueControl({ rule, field, siblingRules, onChange }: FilterValueControlProps) {
  const selectedWorkflowId = useMemo<string | null>(() => {
    const workflowRule = siblingRules.find((entry) => entry.field === 'workflow_id');
    return typeof workflowRule?.value === 'string' && workflowRule.value ? workflowRule.value : null;
  }, [siblingRules]);
  const optionsResult = useFilterOptions(field?.options_source, { selectedWorkflowId });

  if (isBooleanOperator(rule.operator)) {
    return (
      <Text size="tiny" color="var(--ink-lighter)" style={{ paddingLeft: 6 }}>
        no value
      </Text>
    );
  }

  const multi = isMultiValueOperator(rule.operator);

  // ui_control "text" is the BE explicitly requesting free text — honor it
  // even if an options_source were (mistakenly) present alongside.
  if (optionsResult.hasSource && field?.ui_control !== 'text') {
    if (multi) {
      const selectedValues = Array.isArray(rule.value) ? (rule.value as string[]) : [];
      return (
        <MultiSelect
          value={selectedValues}
          onChange={(value) => onChange(value)}
          data={optionsResult.options}
          searchable
          nothingFoundMessage={optionsResult.emptyMessage}
          placeholder={selectedValues.length > 0 ? 'Add…' : `${optionsResult.pickerPlaceholder}…`}
          rightSection={dropdownChevron}
          rightSectionPointerEvents="none"
          disabled={optionsResult.loading && optionsResult.options.length === 0}
          styles={{
            ...inputStyles,
            input: { ...inputStyles.input, paddingTop: 4, paddingBottom: 4 },
            inputField: { height: 20, lineHeight: '20px' },
            pill: {
              fontSize: 14,
              height: 20,
              lineHeight: '20px',
              borderRadius: 6,
              background: 'var(--sky-lighter)',
              color: 'var(--ink-darkest)',
            },
          }}
          size="xs"
        />
      );
    }
    return (
      <Select
        value={typeof rule.value === 'string' ? rule.value : null}
        onChange={(value) => onChange(value ?? '')}
        data={optionsResult.options}
        searchable
        nothingFoundMessage={optionsResult.emptyMessage}
        placeholder={optionsResult.pickerPlaceholder}
        disabled={optionsResult.loading && optionsResult.options.length === 0}
        rightSection={dropdownChevron}
        rightSectionPointerEvents="none"
        styles={inputStyles}
        size="xs"
      />
    );
  }

  // The BE matches raw status names by case-sensitive exact equality (e.g.
  // "Done" ≠ "done"), so free-text status inputs must warn the user; the
  // normalized section/direction dropdowns are the safe path.
  const isStatusNameField = rule.field === 'to_status_name' || rule.field === 'from_status_name';
  const caseSensitiveHint = 'Exact status name from your board — matching is case-sensitive.';

  if (multi) {
    return (
      <MultiValueTextInput
        value={Array.isArray(rule.value) ? (rule.value as string[]) : []}
        placeholder={isStatusNameField ? 'Exact status names, comma-separated' : 'Comma-separated values'}
        title={isStatusNameField ? caseSensitiveHint : undefined}
        description={isStatusNameField ? caseSensitiveHint : undefined}
        onCommit={onChange}
      />
    );
  }
  return (
    <TextInput
      placeholder={
        isStatusNameField
          ? 'Exact status name, e.g. In Progress'
          : field?.value_type === 'uuid'
            ? 'Paste a UUID'
            : 'Value'
      }
      title={isStatusNameField ? caseSensitiveHint : undefined}
      description={isStatusNameField ? caseSensitiveHint : undefined}
      value={typeof rule.value === 'string' ? rule.value : ''}
      onChange={(event) => onChange(event.currentTarget.value)}
      styles={inputStyles}
      size="xs"
    />
  );
}

interface MultiValueTextInputProps {
  value: string[];
  placeholder: string;
  title?: string;
  description?: string;
  onCommit: (value: string[]) => void;
}

/**
 * Free-text input for multi-value operators (`is_any_of`/`is_none_of`) with no
 * options source. Keeps the raw comma-separated string in local state and only
 * parses it into a string[] on blur or Enter — parsing per keystroke would
 * delete a trailing comma the instant it is typed (split → trim → filter
 * drops the empty tail) and eat the spaces of multi-word values like
 * "In Progress", making a second value impossible to start.
 *
 * @param {MultiValueTextInputProps} props - Committed values, input labels, and the commit callback
 * @returns {JSX.Element} A text input that commits parsed values on blur/Enter
 */
function MultiValueTextInput({ value, placeholder, title, description, onCommit }: MultiValueTextInputProps) {
  const joined = value.join(', ');
  const [text, setText] = useState(joined);
  const [lastExternal, setLastExternal] = useState(joined);

  // External resets (preset applied, cascade clear, event switch) must win over
  // stale local text — adjust during render per the React "derived state" pattern.
  if (joined !== lastExternal) {
    setLastExternal(joined);
    setText(joined);
  }

  const commit = (): void => {
    const parsed = text
      .split(',')
      .map((piece) => piece.trim())
      .filter(Boolean);
    const normalized = parsed.join(', ');
    setText(normalized);
    setLastExternal(normalized);
    onCommit(parsed);
  };

  return (
    <TextInput
      placeholder={placeholder}
      title={title}
      description={description}
      value={text}
      onChange={(event) => setText(event.currentTarget.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
      }}
      styles={inputStyles}
      size="xs"
    />
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-dark);
`;

const HelperText = styled.span`
  font-size: 12px;
  color: var(--ink-lighter);
`;

const EventOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const PresetChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PresetChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: var(--secondary-base);
    background: rgba(87, 92, 229, 0.06);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ActiveCount = styled.span`
  font-size: 12px;
  color: var(--ink-lighter);
`;

const RulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RuleCard = styled.div`
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  background: var(--base-white);
  overflow: hidden;
`;

const RuleCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 8px 8px 13px;
  background: var(--sky-lightest);
  border-bottom: 1px solid var(--sky-lighter);
`;

const RuleCardField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
`;

const RuleCardFieldName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--ink-darkest);
`;

// The field picker doubles as the card title: bold header text with no input
// chrome, sized to the field name so the swap chevron sits right beside it.
// `field-sizing` degrades to the input's natural width where unsupported.
const FieldSelectWrapper = styled.div`
  flex: 0 1 auto;
  min-width: 0;

  .mantine-Select-root {
    width: fit-content;
    max-width: 100%;
  }

  input {
    width: auto;
    field-sizing: content;
    font-weight: 700;
    font-size: 14px;
    color: var(--ink-darkest);
    height: 24px;
    min-height: 24px;
    cursor: pointer;
  }
`;

const RuleCardHeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(217, 142, 43, 0.12);
  color: #8a5a12;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RuleCardBody = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  flex-wrap: wrap;
`;

const Connector = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ConnectorLine = styled.span`
  flex: 1;
  height: 1px;
  background: var(--sky-lighter);
`;

const ConnectorPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 11px;
  border: 1px solid var(--sky-light);
  border-radius: 999px;
  background: var(--base-white);
  color: var(--secondary-base);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: var(--secondary-base);
    background: rgba(87, 92, 229, 0.06);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const RequiredBannerGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RequiredBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 12px 11px 14px;
  border-radius: 12px;
  border: 1px solid rgba(217, 142, 43, 0.28);
  background: rgba(217, 142, 43, 0.07);
`;

const RequiredBannerMessage = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  font-weight: 500;
  color: #8a5a12;

  strong {
    font-weight: 700;
    color: #7a4f0f;
  }
`;

const AddRequiredButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: var(--amber-base, #d98e2b);
  color: var(--base-white);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.12s;

  &:hover {
    filter: brightness(0.94);
  }

  &:focus-visible {
    outline: 2px solid var(--amber-base, #d98e2b);
    outline-offset: 2px;
  }
`;

const ValueControlWrapper = styled.div`
  flex: 1 1 160px;
  min-width: 140px;

  /* Mantine's Pill label carries its own 18px line-height, which floats the
     14px chip text toward the top of the 20px pill — center it. */
  .mantine-Pill-label {
    line-height: 20px;
  }
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;

  &:hover:not(:disabled) {
    background: var(--sky-lighter);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const AddRuleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  border: 1.5px dashed var(--sky-base);
  border-radius: 12px;
  background: var(--sky-lightest);
  color: var(--secondary-base);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover {
    border-color: var(--secondary-base);
    background: rgba(87, 92, 229, 0.05);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

export type { AgentAutomationTriggerConfigProps };
